"use client";

import { mobileSidebarLinks } from "@/constants/navigation";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import Link from "next/link";
import { usePathname } from "next/navigation";
import useUser from "@/hooks/useUser";
import {
  Menu,
  User,
  Bell,
  Target,
  Trophy,
  Bookmark,
  Settings,
  CalendarIcon,
  Map,
  CreditCard,
  Ticket,
  TestTubes,
  Briefcase,
  Stethoscope,
  Dna,
  Utensils
} from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

interface Props {
  username: string;
}

const MobileSidebar = ({ username }: Props) => {
  const pathname = usePathname();
  const user = useUser();

  const getIcon = (title: string, isActive: boolean) => {
    const props = { 
      size: 24,
      className: `transition-colors ${isActive ? 'text-primary' : 'text-muted-foreground'}`
    };

    switch (title) {
      case "Journey":
        return <User {...props} />;
      case "Notifications":
        return <Bell {...props} />;
      case "Challenges":
        return <Target {...props} />;
      case "Leaderboard":
        return <Trophy {...props} />;
      case "Bookmarks":
        return <Bookmark {...props} />;
      case "Calendars":
        return <CalendarIcon {...props} />;
      case "Map":
        return <Map {...props} />;
      case "Transactions":
        return <CreditCard {...props} />;
      case "Events":
        return <Ticket {...props} />;
      case "Trials":
        return <TestTubes {...props} />;
      case "Jobs":
        return <Briefcase {...props} />;
      case "Consultations":
        return <Stethoscope {...props} />;
      case "Genomics":
        return <Dna {...props} />;
      case "Nutrition":
        return <Utensils {...props} />;
      default:
        return null;
    }
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button className="flex sm:hidden items-center justify-center">
          <Menu size={24} className="text-foreground" />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="flex flex-col bg-black/95 border-r border-r-gray-700 p-0">
        <div className="flex-none p-6">
          <div className="flex items-center gap-4">
            <Link href={`/${username}`}>
              <Avatar className="w-10 h-10">
                <AvatarImage src={user?.user?.image || ""} alt={user?.user?.name || "User"} />
                <AvatarFallback className="bg-gray-800 dark:bg-gray-800">
                  {user?.user?.name?.[0] || "U"}
                </AvatarFallback>
              </Avatar>
            </Link>
            <div className="flex flex-col">
              <Link 
                href={`/${username}`}
                className="text-sm font-semibold hover:text-gray-300"
              >
                {username}
              </Link>
              <Link 
                href="/journey"
                className="text-xs text-gray-400 hover:text-gray-300"
              >
                View Journey
              </Link>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-6 pb-6">
          <div className="flex flex-col gap-2">
            {mobileSidebarLinks?.map((link) => {
              if (!link.href && link.title === "Journey") {
                link.href = `/${username}`;
              }
              
              const isActive = pathname === link.href;

              return (
                <Link
                  key={link.title}
                  href={link.href}
                  className={`flex items-center gap-4 p-3 rounded-lg transition-colors ${
                    isActive 
                      ? "bg-gray-800/50" 
                      : "hover:bg-gray-800/30"
                  }`}
                >
                  {getIcon(link.title, isActive)}
                  <span className={`text-sm ${
                    isActive ? "font-medium text-primary" : "text-muted-foreground"
                  }`}>
                    {link.title}
                  </span>
                </Link>
              );
            })}
          </div>
        </nav>

        <div className="flex-none p-6 border-t border-gray-700">
          <Link
            href="/settings"
            className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-800/30"
          >
            <Settings size={24} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Settings</span>
          </Link>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileSidebar;